import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLatest } from "@/hooks/useLatest";
import RobotFace from "@/components/RobotFace";
import VoiceMode from "@/components/VoiceMode";
import EditImageSheet from "@/components/EditImageSheet";
import SaveSubjectDialog from "@/components/SaveSubjectDialog";
import ShareChatButton from "@/components/ShareChatButton";
import MessageBubble from "@/components/chat/MessageBubble";
import ChatWelcome from "@/components/chat/ChatWelcome";
import { ACCEPT_ATTR, MAX_FILES } from "@/lib/chatAttachments";
import ConversationsRail from "@/components/ConversationsRail";
import ChatComposer from "@/components/ChatComposer";
import CanvasPanel from "@/components/CanvasPanel";
import useChatAttachments from "@/hooks/useChatAttachments";
import useChatMedia from "@/hooks/useChatMedia";
import useChatStream from "@/hooks/useChatStream";
import * as L from "lucide-react";

/**
 * ChatScreen — the primary chat surface. State/handlers are colocated here
 * because MessageBubble and ChatComposer both need direct access, but the
 * heavy lifting is delegated to three focused hooks:
 *
 *   • useChatAttachments — file pick/paste/drag lifecycle for the composer.
 *   • useChatMedia       — inline image/video/sound generation + share/edit.
 *   • useChatStream      — the SSE send() pipeline and event parsing.
 */

/**
 * RammyStatusPill — the header status indicator. Uses precise, warm language
 * ("Ready when you are" instead of a permanently-on "Listening") so it never
 * suggests the microphone is always active. Colors + micro-animation cue
 * come from a small state map so future states (Checking the web / Using
 * your calendar) plug in without conditional trees.
 */
const STATUS_STATES = {
  ready:     { label: "Ready when you are",  dot: "bg-slate-400",           bg: "bg-[var(--rammy-surface-alt)] text-[var(--rammy-text-sec)]",       anim: "" },
  listening: { label: "Listening",           dot: "bg-[var(--rammy-cyan)]",  bg: "bg-[var(--rammy-cyan-soft)] text-[var(--rammy-cyan-dark)]",       anim: "animate-pulse" },
  thinking:  { label: "Thinking",            dot: "bg-[var(--rammy-lavender)]", bg: "bg-[var(--rammy-lavender-soft)] text-[#5A4C8C]",              anim: "animate-pulse" },
  speaking:  { label: "Speaking",            dot: "bg-[var(--rammy-cyan-dark)]", bg: "bg-[var(--rammy-cyan-soft)] text-[var(--rammy-cyan-dark)]",  anim: "animate-pulse" },
  web:       { label: "Checking the web",    dot: "bg-blue-500",            bg: "bg-blue-50 text-blue-700",                                       anim: "animate-pulse" },
  calendar:  { label: "Using your calendar", dot: "bg-amber-500",           bg: "bg-amber-50 text-amber-800",                                     anim: "animate-pulse" },
  sad:       { label: "Here for you",        dot: "bg-rose-400",            bg: "bg-rose-50 text-rose-700",                                       anim: "" },
};

function RammyStatusPill({ busy, expression, live }) {
  let key = "ready";
  if (live === "web") key = "web";
  else if (live === "calendar") key = "calendar";
  else if (busy && expression === "speaking") key = "speaking";
  else if (busy) key = "thinking";
  else if (expression === "sad") key = "sad";
  const s = STATUS_STATES[key];
  return (
    <div
      className={`mt-1.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-wide ${s.bg}`}
      data-testid="chat-status"
      data-state={key}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${s.dot} ${s.anim}`} />
      <span>{s.label}</span>
    </div>
  );
}

export function ChatScreen({ user, refresh, expression, setExpression, initialConversationId, onConsumeInitial, go }) {
  // Conversations rail visibility. On desktop, we auto-collapse the rail
  // the moment a user starts a real conversation so the chat gets more
  // room to breathe. First-load with a resumed conv keeps it collapsed;
  // brand-new empty chat keeps it open. Users can toggle any time.
  const [railOpen, setRailOpen] = useState(false);
  const [railHidden, setRailHidden] = useState(!!initialConversationId);
  const [msgs, setMsgs] = useState([
    { role: "assistant", content: `Hi ${user?.preferred_name || user?.name?.split(" ")[0] || "there"} — I'm Rammy. What's on your mind today?` },
  ]);
  const [input, setInput] = useState("");
  const [convId, setConvId] = useState(null);
  // See `useLatest` — kept authoritative for the SSE meta race described in
  // iteration 47 (rapid barge-in voice turns fragmenting into new convs).
  const convIdRef = useLatest(convId);

  // Copy Studio → Chat prefill: when the user picks a direction in Copy
  // Studio, we stash the composed prompt on window and route them to chat.
  // Consume it here on mount so the composer opens ready to send.
  useEffect(() => {
    const prefill = typeof window !== "undefined" && window.__RAMMY_CHAT_PREFILL__;
    if (prefill && typeof prefill === "string") {
      setInput(prefill);
      window.__RAMMY_CHAT_PREFILL__ = null;
    }
  }, []);
  const [busy, setBusy] = useState(false);
  const [savedMems, setSavedMems] = useState([]);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [editingImage, setEditingImage] = useState(null);
  // A picture the user wants Rammy to remember as a cast member.
  const [subjectImage, setSubjectImage] = useState(null);
  const [canvasArtifact, setCanvasArtifact] = useState(null);
  const [convRefreshKey, setConvRefreshKey] = useState(0);
  // Live tool signal — set by the SSE stream when the LLM invokes a
  // side-tool (web search, calendar lookup). Drives the status pill's
  // "Checking the web" / "Using your calendar" states. Cleared on turn end.
  const [liveTool, setLiveTool] = useState(null); // null | "web" | "calendar"
  const voiceRef = useRef(null);
  const voiceModeRef = useRef(false);
  // Focus mode — null | "walking" | "focus". Set by the toggle chip in
  // VoiceMode (voice) and by an inline chip in ChatComposer (typed). The
  // ref lives here (not inside the hook) so the current value is available
  // to `send()` without triggering a rebuild of the SSE closure on every
  // toggle.
  const [focusMode, setFocusMode] = useState(null);
  const focusModeRef = useLatest(focusMode);
  const endRef = useRef(null);

  // Hooks: attachment lifecycle + media generation + SSE stream.
  const {
    attachments, setAttachments, dragOver, onPick, onPaste,
    dragProps, removeAttachment,
  } = useChatAttachments();

  const media = useChatMedia({ setMsgs, setBusy, user });

  const send = useChatStream({
    input, setInput,
    msgs, setMsgs,
    convId, setConvId, convIdRef,
    attachments, setAttachments,
    setBusy, setExpression, setSavedMems,
    voiceRef, voiceModeRef, focusModeRef,
    handleInlineGenerate: media.handleInlineGenerate,
  });

  // Auto-scroll to the tail on every new message / tick.
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  // Auto-collapse the conversations rail the FIRST time the user sends a
  // message so the transcript gets the full width to breathe. We only fire
  // once (via a ref-guard) so re-opening the rail manually mid-chat sticks.
  const autoCollapsedRef = useRef(false);
  useEffect(() => {
    if (autoCollapsedRef.current) return;
    const hasUserMsg = msgs.some(m => m.role === "user");
    if (hasUserMsg || busy) {
      autoCollapsedRef.current = true;
      setRailHidden(true);
    }
  }, [msgs, busy]);

  // Consume a resume-request coming in from AppShell (e.g. Home's "Continue
  // where you left off" card). We use a ref-guard so a rapid remount doesn't
  // re-fetch the same conversation twice.
  const consumedInitialRef = useRef(null);
  useEffect(() => {
    if (!initialConversationId) return;
    if (consumedInitialRef.current === initialConversationId) return;
    consumedInitialRef.current = initialConversationId;
    loadConversation(initialConversationId).finally(() => onConsumeInitial?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConversationId]);

  // Consume a pending "check-in opener" left in sessionStorage by the Home
  // callback card. One-shot — we clear it the moment we read it so a page
  // refresh doesn't repeat the opener.
  useEffect(() => {
    try {
      const opener = sessionStorage.getItem("rammy:pending_opener");
      if (opener) {
        sessionStorage.removeItem("rammy:pending_opener");
        setInput(opener);
      }
      // Home's Ring hands voice mode over through the same one-shot channel.
      if (sessionStorage.getItem("rammy:open_voice")) {
        sessionStorage.removeItem("rammy:open_voice");
        voiceModeRef.current = true;
        setVoiceOpen(true);
      }
    } catch (_) { /* private-mode / disabled storage — silent */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bump the rail whenever we start or send in a conversation so the sidebar
  // shows fresh titles, snippets, and updated_at without a manual refresh.
  useEffect(() => {
    if (convId && !busy) {
      const t = setTimeout(() => setConvRefreshKey(k => k + 1), 400);
      return () => clearTimeout(t);
    }
  }, [convId, busy, msgs.length]);

  /** Load a past conversation into the chat view. */
  const loadConversation = async (cid) => {
    if (!cid || cid === convId) return;
    try {
      const { data } = await api.get(`/conversations/${cid}`);
      const loaded = (data.messages || []).map(m => ({ role: m.role, content: m.content }));
      setMsgs(loaded);
      setConvId(cid);
      convIdRef.current = cid;   // keep ref in lockstep with state
      setSavedMems([]);
      setAttachments([]);
      setCanvasArtifact(null);
    } catch (_) {
      toast.error("Couldn't open that conversation");
    }
  };

  /** Start a fresh conversation. */
  const newChat = () => {
    setMsgs([
      { role: "assistant", content: `Hi ${user?.preferred_name || user?.name?.split(" ")[0] || "there"} — I'm Rammy. What's on your mind today?` },
    ]);
    setConvId(null);
    convIdRef.current = null;   // sync the ref so a subsequent send() creates a fresh conv
    setInput("");
    setSavedMems([]);
    setAttachments([]);
    setCanvasArtifact(null);
    setConvRefreshKey(k => k + 1);
  };

  /** Regenerate the last assistant reply.
   *  Slices the last user + last assistant off `msgs`, then re-runs `send()`
   *  with the same user text (plus an optional invisible <rammy:style> marker
   *  the LLM knows how to interpret) so the SSE pipeline produces a fresh
   *  reply reshaped by that hint. */
  const regenerate = (styleKey) => {
    if (busy) return;
    let userIdx = -1;
    for (let k = msgs.length - 1; k >= 0; k--) {
      if (msgs[k].role === "user") { userIdx = k; break; }
    }
    if (userIdx < 0) return;
    const baseText = (msgs[userIdx].content || "").trim();
    if (!baseText) return;
    const text = styleKey
      ? `${baseText}\n\n<rammy:style>${styleKey}</rammy:style>`
      : baseText;
    setMsgs(m => m.slice(0, userIdx));
    setTimeout(() => send(text), 0);
  };

  /** Load the last user message back into the composer so the user can edit it. */
  const editLastUser = () => {
    if (busy) return;
    let userIdx = -1;
    for (let k = msgs.length - 1; k >= 0; k--) {
      if (msgs[k].role === "user") { userIdx = k; break; }
    }
    if (userIdx < 0) return;
    setInput(msgs[userIdx].content || "");
    setMsgs(m => m.slice(0, userIdx));
    setTimeout(() => {
      const el = document.querySelector('[data-testid="chat-input"]');
      if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
    }, 30);
  };

  // Compute trailing "last user" and "last assistant" indices once so
  // MessageBubble knows where to render Edit / Regenerate.
  let lastUserIdx = -1, lastAssistIdx = -1;
  for (let k = msgs.length - 1; k >= 0; k--) {
    if (lastAssistIdx < 0 && msgs[k].role === "assistant") lastAssistIdx = k;
    if (lastUserIdx   < 0 && msgs[k].role === "user")      lastUserIdx   = k;
    if (lastUserIdx >= 0 && lastAssistIdx >= 0) break;
  }

  // Callback the language-suggestion chip hits on both Switch AND Dismiss.
  // We refresh the user profile so any subsequent turn picks up the new
  // language (system prompt is composed off `user.language`), and clear
  // the `suggest_language` off the current bubble's trace so the chip
  // doesn't re-render on the same message.
  const onLanguageChanged = useCallback(() => {
    refresh?.();
    setMsgs(prev => prev.map(m => (
      m.trace?.suggest_language
        ? { ...m, trace: { ...m.trace, suggest_language: null } }
        : m
    )));
  }, [refresh]);

  return (
    <div
      className="flex h-full min-h-0 relative"
      data-testid="screen-chat"
      {...dragProps}
    >
      {/* Conversations rail — desktop: permanent left panel (auto-collapses
          to a slim strip once the user starts chatting so the transcript
          has more room). Mobile: overlay drawer toggled via the "History"
          pill in the chat header. */}
      <div className="hidden md:flex">
        <ConversationsRail
          activeId={convId}
          refreshKey={convRefreshKey}
          onSelect={(id) => { setRailHidden(true); loadConversation(id); }}
          onNew={() => { setRailHidden(false); newChat(); }}
          hidden={railHidden}
          onToggleHidden={setRailHidden}
        />
      </div>
      {railOpen && (
        <div className="md:hidden fixed inset-0 z-40 flex" data-testid="chat-rail-mobile">
          <button
            type="button"
            aria-label="Close conversations"
            className="absolute inset-0 bg-black/40"
            onClick={() => setRailOpen(false)}
          />
          <div className="relative z-10 h-full">
            <ConversationsRail
              activeId={convId}
              refreshKey={convRefreshKey}
              onSelect={(id) => { setRailOpen(false); loadConversation(id); }}
              onNew={() => { setRailOpen(false); newChat(); }}
            />
          </div>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col relative">
        {/* Ambient state-driven canvas — a very subtle gradient that shifts
            with Rammy's mood, giving the whole surface an alive quality
            without competing with the content. Sits behind everything. */}
        <div
          className="rammy-ambient"
          data-state={
            busy && expression === "speaking" ? "speaking"
            : busy ? "thinking"
            : voiceOpen ? "listening"
            : "idle"
          }
          aria-hidden
        />
        {/* Mobile-only "History" pill so users can open the rail without a permanent left panel. */}
        <div className="md:hidden px-3 pt-2 flex items-center gap-2">
          <button
            type="button"
            onClick={() => setRailOpen(true)}
            className="btn-pill h-8 text-[11px] px-3 bg-[var(--rm-surface)] border border-[var(--rammy-border)] text-[var(--rammy-text)] inline-flex items-center gap-1.5"
            data-testid="chat-mobile-history"
          >
            <L.MessagesSquare size={12}/> History
          </button>
          <button
            type="button"
            onClick={newChat}
            className="btn-pill h-8 text-[11px] px-3 bg-[var(--rammy-cyan)]/20 border border-[var(--rammy-cyan)]/40 text-[var(--rammy-cyan-dark)] inline-flex items-center gap-1.5"
            data-testid="chat-mobile-new"
          >
            <L.Plus size={12}/> New chat
          </button>
        </div>
        {dragOver && (
          <div className="absolute inset-0 z-40 pointer-events-none flex items-center justify-center" data-testid="chat-drop-overlay">
            <div className="absolute inset-4 rounded-3xl border-2 border-dashed border-[var(--rammy-cyan)] bg-[var(--rm-surface)]/85 backdrop-blur-md"/>
            <div className="relative text-center px-8">
              <div className="mx-auto w-14 h-14 rounded-2xl bg-[var(--rammy-cyan)]/25 flex items-center justify-center rammy-shadow">
                <L.Paperclip size={26} className="text-[var(--rammy-cyan-dark)]"/>
              </div>
              <div className="mt-3 text-xl md:text-2xl font-display">Drop it — I&rsquo;ll take a look.</div>
              <div className="mt-1 text-xs text-[var(--rammy-text-sec)]">JPEG, PNG or WEBP · up to 5 MB · 3 images</div>
            </div>
          </div>
        )}
        <div className="px-5 pt-5 pb-4 flex items-center gap-4 relative z-10">
          {/* Redesigned Rammy avatar — a warm ivory squircle with a soft
              turquoise glow orb + tiny inner core. Replaces the black-square
              face-slit look; reads as alive rather than robotic. */}
          <div
            className="rammy-orb w-14 h-14 md:w-16 md:h-16 rounded-2xl"
            data-mood={busy ? (expression === "speaking" ? "speaking" : "thinking") : expression === "sad" ? "sad" : "idle"}
            aria-label="Rammy"
          />
          <div className="flex-1 min-w-0 relative">
            <div className="flex items-baseline gap-2 flex-wrap">
              <div className="font-display text-xl md:text-2xl leading-tight truncate">
                {"Rammy"}
              </div>
              <span className="text-[10px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold">
                {(user?.voice || "hale").charAt(0).toUpperCase() + (user?.voice || "hale").slice(1)} voice
              </span>
            </div>
            <RammyStatusPill busy={busy} expression={expression} live={liveTool} />
            <div className="text-[12.5px] text-[var(--rammy-text-muted)] italic font-serif mt-1 truncate">
              Warm, present — remembers what matters.
            </div>
          </div>
          {convId && msgs.length >= 2 && (
            <ShareChatButton conversationId={convId} data-testid="chat-share"/>
          )}
        </div>
        <div className="flex-1 overflow-y-auto subtle-scroll px-4 py-4 space-y-3" data-testid="chat-thread">
          {/* Fresh chat (no convId loaded) → show the welcome hero.
              An opened conversation ALWAYS renders its messages, even if it
              only has one stored (e.g., a lost-reply case) — otherwise we'd
              silently show the welcome hero over the user's real message. */}
          {!convId && msgs.length <= 1 && (
            <ChatWelcome
              user={user}
              expression={expression}
              onQuickAction={(text) => send(text)}
              onOpenVoice={() => { voiceModeRef.current = true; setVoiceOpen(true); }}
            />
          )}
          {(convId || msgs.length > 1) && msgs.map((m, i) => (
            <MessageBubble
              key={i}
              m={m}
              i={i}
              prev={i > 0 ? msgs[i - 1] : null}
              isLastUser={i === lastUserIdx}
              isLastAssistant={i === lastAssistIdx}
              busy={busy}
              sharingIdx={media.sharingIdx}
              onEditImage={setEditingImage}
              onSend={send}
              onOpenCanvas={setCanvasArtifact}
              onDownloadVideo={media.downloadVideo}
              onDownloadSound={media.downloadSound}
              onKeepImageMemory={media.keepImageMemory}
              onShareImageAsCard={media.shareImageAsCard}
              onSaveSubject={setSubjectImage}
              onRegenerate={regenerate}
              onEditLastUser={editLastUser}
              onSuggestLanguageAccepted={onLanguageChanged}
              onSuggestLanguageDismissed={onLanguageChanged}
            />
          ))}
          {/* Recovery affordance — if an opened conversation ends on a user
              message (no assistant reply was saved, e.g. the stream failed
              or was aborted server-side), show a one-click "Retry" chip so
              the user can resurrect the response instead of staring at a
              dead-end. */}
          {convId && !busy && msgs.length >= 1 && msgs[msgs.length - 1]?.role === "user" && (
            <div
              className="rounded-2xl bg-amber-50 border border-amber-200 px-3 py-2.5 flex items-start gap-2"
              data-testid="chat-retry-hint"
            >
              <L.AlertCircle size={14} className="text-amber-700 mt-0.5 shrink-0"/>
              <div className="flex-1 text-[12px] text-amber-900 leading-snug">
                No reply was saved for this message. Rammy may have been interrupted.
                <button
                  onClick={() => {
                    const lastUser = msgs[msgs.length - 1]?.content || "";
                    if (!lastUser) return;
                    // Remove the orphaned user message, then re-send so we
                    // don't end up with a duplicate in the thread.
                    setMsgs(m => m.slice(0, -1));
                    setTimeout(() => send(lastUser), 0);
                  }}
                  className="ml-1 underline font-semibold hover:text-amber-950"
                  data-testid="chat-retry-btn"
                >
                  Retry now
                </button>
              </div>
            </div>
          )}
          <div ref={endRef}/>
        </div>
        <div className="border-t border-[var(--rammy-border)] bg-[var(--rm-surface)]">
          {savedMems.length > 0 && (
            <div className="mx-3 mt-3 rounded-2xl border border-[var(--rammy-cyan)]/40 bg-[var(--rammy-cyan)]/10 px-3 py-2 flex items-start gap-2 fade-up" data-testid="chat-memory-saved">
              <L.Brain size={14} className="text-[var(--rammy-cyan-dark)] mt-0.5 shrink-0"/>
              <div className="text-xs leading-relaxed">
                <span className="font-medium text-[var(--rammy-text)]">Rammy remembered {savedMems.length === 1 ? "this" : `${savedMems.length} things`}:</span>{" "}
                <span className="text-[var(--rammy-text-sec)]">
                  {savedMems.map(m => m.title).join(" · ")}
                </span>
              </div>
            </div>
          )}
          <ChatComposer
            input={input}
            setInput={setInput}
            attachments={attachments}
            removeAttachment={removeAttachment}
            onPickFiles={onPick}
            onPaste={onPaste}
            onSend={() => send()}
            onOpenVoice={() => { voiceModeRef.current = true; setVoiceOpen(true); }}
            onCreateImage={media.createImage}
            onCreateVideo={media.createVideo}
            onCreateSound={media.createSound}
            busy={busy}
            maxFiles={MAX_FILES}
            accept={ACCEPT_ATTR}
            onExploreDirections={(brief) => {
              // Stash the current composer text as a Copy Studio brief and
              // route the user to the 3-directions flow. When they pick a
              // direction there, they come back to chat with a prompt
              // pre-filled via `window.__RAMMY_CHAT_PREFILL__`.
              try {
                window.__RAMMY_COPY_STUDIO_BRIEF__ = brief;
              } catch { /* ignore */ }
              go?.("copy-studio");
            }}
          />
        </div>
        {voiceOpen && (
          <VoiceMode
            voice={user?.voice || "hale"}
            conversationId={convId}
            onExchange={({ user: uTxt, assistant, conversationId, latency }) => {
              // Streaming voice pipeline handles STT/LLM/TTS server-side; here we
              // just append the exchange to the local transcript so the chat
              // panel reflects what happened while the overlay was open.
              const now = new Date().toISOString();
              setMsgs((m) => [
                ...m,
                { id: `v-u-${now}`, role: "user", content: uTxt || "", created_at: now, voice: true },
                { id: `v-a-${now}`, role: "assistant", content: assistant || "", created_at: now, voice: true, latency },
              ]);
            }}
            onConversationCreated={(cid) => {
              if (cid && !convId) {
                setConvId(cid);
                convIdRef.current = cid;
              }
            }}
            onClose={() => { voiceModeRef.current = false; setVoiceOpen(false); }}
            onOpenMyVoice={() => {
              voiceModeRef.current = false;
              setVoiceOpen(false);
              if (go) go("myvoice");
            }}
            focusMode={focusMode}
            onFocusModeChange={setFocusMode}
          />
        )}
        {editingImage && (
          <EditImageSheet
            imageDataUrl={editingImage.dataUrl}
            onClose={() => setEditingImage(null)}
            onEdited={media.onImageEdited}
          />
        )}
        {subjectImage && (
          <SaveSubjectDialog
            imageSrc={subjectImage}
            onClose={() => setSubjectImage(null)}
          />
        )}
        {canvasArtifact && (
          <CanvasPanel
            artifact={canvasArtifact}
            onClose={() => setCanvasArtifact(null)}
          />
        )}
      </div>
    </div>
  );
}
